import React, { useEffect, useLayoutEffect, useState } from 'react';
import { DownloadSimple, Eye } from '@phosphor-icons/react';
import AdminContainer from '../../../Utility/AdminContainer';
import useLogFiles from '../../../../Hooks/useCompliance';
import AdminBox from '../../../Utility/AdminBox';
import AdminViewHeader from '../../../Utility/AdminViewHeader';
import AdminViewContent from '../../../Utility/AdminViewContent';
import { PaginationWrapper } from '../../../Utility/PaginationWrapper';
import { PaginationListType } from '../../../../Types/PaginationParams';
import { LogFile, ModalData } from '../../../../Types';
import { complianceService } from '../../../../Services/complianceService';
import useSearchFilter from '../../../../Hooks/useSearchFilter';
import SearchFilter from '../../../Utility/SearchFilter';
import ToggleSwitch from '../../../Utility/ToggleSwitch';
import { LogType } from '../../../../Types/Enums';
import LogFileDialog from './LogFileDialog';
import { useLocation } from 'react-router-dom';
import { setModal } from '../../../../Store/Actions/modalActions';
import { useDispatch } from 'react-redux';

let currentLogFiles: { [key: string]: LogFile[] } = {};
const LOGTYPE2LISTTYPE = {
    [LogType.Audit as string]: PaginationListType.AuditLogFiles,
    [LogType.Auth as string]: PaginationListType.AuthLogFiles,
    [LogType.Dlp as string]: PaginationListType.DlpLogFiles,

    [LogType.Scim as string]: PaginationListType.ScimLogFiles,
    [LogType.System as string]: PaginationListType.SystemLogFiles,
    [LogType.Security as string]: PaginationListType.SecurityLogFiles,
};

const LogFiles = () => {
    const location = useLocation().pathname;
    const [logTypes, setLogTypes] = useState<LogType[]>([]);
    const dispatch = useDispatch();
    const { i18n, getBreakpoint, windowWidth, download, toLocalDateTime, logType, setLogType } = useLogFiles({
        initialLogType: location.includes('admin') ? LogType.Scim : LogType.Audit,
    });

    useLayoutEffect(() => {
        setLogType(location.includes('admin') ? LogType.Scim : LogType.Audit);
        setLogTypes(
            location.includes('admin')
                ? [LogType.Scim, LogType.System, LogType.Security]
                : [LogType.Audit, LogType.Auth, LogType.Dlp],
        );
    }, [location, setLogType]);

    const { query, localQuery, searchActions } = useSearchFilter({
        initialQuery: null,
    });

    const getLogFilesInterceptor = React.useCallback(
        async (page: number, itemsPerPage: number, _: any, query?: string | null) => {
            const res = await complianceService.getLogFiles(logType, page, itemsPerPage, query);
            currentLogFiles[logType as string] = res.data.data;
            return res;
        },
        [logType],
    );

    const GRID_COLS = 'grid-cols-[minmax(157px,300px),minmax(157px,200px),minmax(157px,1fr)]';

    return (
        <div className={`admin-container flex relative flex-col`}>
            <AdminContainer>
                <div className="mb-5 w-full">
                    <ToggleSwitch
                        options={logTypes.map((type) => ({
                            label: i18n.translate({ id: (type as string) + '.title', sourceName: 'LogFiles' }),
                            function: () => setLogType(type),
                            active: logType === type,
                        }))}
                    />
                </div>
                <AdminBox classString="px-4">
                    <SearchFilter
                        value={localQuery}
                        onChange={searchActions.setLocalQuery}
                        onApply={searchActions.applyQuery}
                        onClear={searchActions.clearQuery}
                    />
                    <div className="hidden md:block">
                        <AdminViewHeader
                            gridCols={GRID_COLS}
                            cols={{
                                cols: [
                                    {
                                        text: i18n.translate({ id: 'card.name', sourceName: 'LogFiles' }),
                                    },
                                    {
                                        text: i18n.translate({ id: 'card.modifiedAt', sourceName: 'LogFiles' }),
                                    },
                                ],
                                button: !(currentLogFiles[logType as string]?.length >= 1)
                                    ? undefined
                                    : {
                                          text: i18n.translate({
                                              id: 'card.downloadTheseLogFiles',
                                              sourceName: 'LogFiles',
                                          }),
                                          function: () => download(logType, currentLogFiles[logType as string]),
                                      },
                            }}
                        />
                        <hr className="border-layout-borderColor mt-5" />
                    </div>

                    <PaginationWrapper<LogFile>
                        listType={LOGTYPE2LISTTYPE[logType as string]}
                        itemsFunction={getLogFilesInterceptor}
                        itemsPerPage={15}
                        query={query ?? null}
                        renderItem={(logFile: LogFile, index: number) => (
                            <AdminViewContent
                                key={logFile.name}
                                mobile={windowWidth < getBreakpoint('md')}
                                identifier={logFile.name}
                                gridCols={GRID_COLS}
                                cols={{
                                    optionalCols: [
                                        {
                                            content: logFile.name,
                                        },
                                        {
                                            content: toLocalDateTime(logFile.modifiedAt),
                                        },
                                    ],
                                    button: {
                                        buttons: [
                                            {
                                                variant: 'secondaryRound',
                                                onClick: () => {
                                                    dispatch(
                                                        setModal({
                                                            state: 'open',
                                                            title: i18n.translate({
                                                                id: 'logFilePreview',
                                                                sourceName: 'LogFiles',
                                                            }),
                                                            modalSize: 'full',
                                                            showLeftButton: false,
                                                            showRightButton: false,
                                                            content: (
                                                                <LogFileDialog
                                                                    data={{
                                                                        logType: logType,
                                                                        filename: logFile.name,
                                                                    }}
                                                                    title={
                                                                        i18n.translate({
                                                                            id: 'logFileDialog.title',
                                                                            sourceName: 'LogFiles',
                                                                        }) +
                                                                        ' ' +
                                                                        logFile?.name
                                                                    }
                                                                />
                                                            ),
                                                        } as ModalData),
                                                    );
                                                },
                                                icon: <Eye />,
                                            },
                                            {
                                                variant: 'primaryRound',
                                                onClick: () => download(logType, [logFile]),
                                                icon: <DownloadSimple />,
                                            },
                                        ],
                                    },
                                    mobileOrder: {
                                        main: logFile.name,
                                        optionalCols: [toLocalDateTime(logFile.modifiedAt)],
                                    },
                                }}
                            />
                        )}
                    />
                </AdminBox>
            </AdminContainer>
        </div>
    );
};

export default LogFiles;
